import { useCallback, useState } from 'react';
import { formatFlightHourBucket, formatOperatorHourBucket } from '@/lib/disruptions/airport-tz';

// Which clock the departures board reads in (PO 2026-10-28: «que los cálculos los haga
// basado en el horario del operador y que pueda switchear con un botón»): `local` — the
// operator's own computer (the default: one clock for every row, so the hour separators
// and «Ahora» follow the real order); `origin` — each departure airport's. Both times
// are always on the row; this picks the one set big and the separators' clock. Kept per
// viewer in the browser — a convenience, never shared state.

export type ClockBasis = 'local' | 'origin';

const KEY = 'nexa:flights:clock';

/** The hour separator of a row in the list's clock: one clock (Local), or the airport's with its code (Origen). */
export function hourBucketOf(iso: string | null | undefined, origin: string | null | undefined, clock: ClockBasis): string | null {
  return clock === 'origin' ? formatFlightHourBucket(iso, origin) : formatOperatorHourBucket(iso);
}

function read(): ClockBasis {
  try {
    return window.localStorage.getItem(KEY) === 'origin' ? 'origin' : 'local';
  } catch {
    return 'local';
  }
}

/** The board's clock and its setter (remembered in this browser when it can be). */
export function useClockBasis(): [ClockBasis, (next: ClockBasis) => void] {
  const [clock, setClock] = useState<ClockBasis>(read);
  const set = useCallback((next: ClockBasis) => {
    setClock(next);
    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      // private window / blocked storage: the choice lasts this visit only
    }
  }, []);
  return [clock, set];
}
